<template>
    <div>
        <!-- Mobile Layout -->
        <section class="block md:hidden space-y-4 mb-5">
            <!-- Search Row -->
            <div class="flex items-center">
                <SearchInput :loading="loading" placeholder="Search by message..." @search="(input) => (searchQuery = input)" class="w-full" />
            </div>

            <!-- Filters Row -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <MultiSelect
                    v-model="selectedLogLevelKeys"
                    :options="listOfLogLevels"
                    :placeholder="'Log levels'"
                    :disabled="loading"
                    :option-label="'name'"
                    :track-by="'key'"
                    :show-tags="false"
                    :modal-popover="true"
                    :as-child="true"
                    class="w-full">
                    <Button variant="outline" :disabled="loading" class="h-9 inline-flex w-full justify-center items-center px-3 text-sm font-medium">
                        <Filter
                            :class="['h-4 w-4 mr-1', selectedLogLevelKeys.length > 0 ? 'text-primary' : '']"
                            :fill="selectedLogLevelKeys.length > 0 ? 'currentColor' : 'none'" />
                        <span class="px-1">
                            {{ selectedLogLevelKeys.length > 0 ? `${selectedLogLevelKeys.length} selected` : 'Log levels' }}
                        </span>
                        <ChevronDown class="-mr-1 ml-2 h-5 w-5" />
                    </Button>
                </MultiSelect>

                <ConnectorFilter
                    v-model="selectedConnectors"
                    :connectors="connectors"
                    :loading="loading"
                    :show-tags="false"
                    placeholder="Filter by Connector"
                    :enable-filter-icon="true"
                    :include-all-option="false"
                    class="w-full" />
            </div>

            <!-- Time Range & Controls Row -->
            <div class="flex gap-3 items-center">
                <TimeRangeFilter
                    v-model="selectedTimeRange"
                    :loading="loading"
                    placeholder="Select time range"
                    :include-live-option="false"
                    :include-short-ranges="true"
                    :hide-options="[1209600, 2592000, 7776000, 31536000]"
                    class="flex-1"
                    @update:model-value="onTimeRangeChange" />

                <Button
                    v-if="isPlayPauseButtonEnabled"
                    variant="default"
                    size="icon"
                    class="rounded-full my-1 flex-shrink-0"
                    :class="{ 'pointer-events-none opacity-50': !isPlayPauseButtonEnabled }"
                    @click="onPlayPauseClick">
                    <Play v-if="playLiveFeed" class="h-5 w-5" />
                    <Pause v-else class="h-5 w-5" />
                </Button>
            </div>
        </section>

        <!-- Desktop Layout -->
        <section class="hidden md:flex justify-between items-center mb-5">
            <div class="flex items-center">
                <SearchInput :loading="loading" placeholder="Search by message..." @search="(input) => (searchQuery = input)" />
            </div>
            <div class="flex items-center gap-3">
                <MultiSelect
                    v-model="selectedLogLevelKeys"
                    :options="listOfLogLevels"
                    :placeholder="'Log levels'"
                    :disabled="loading"
                    :option-label="'name'"
                    :track-by="'key'"
                    :show-tags="false"
                    :modal-popover="true"
                    :as-child="true">
                    <Button variant="outline" :disabled="loading" class="h-9 inline-flex w-full justify-center items-center px-3 text-sm font-medium">
                        <Filter
                            :class="['h-4 w-4 mr-1', selectedLogLevelKeys.length > 0 ? 'text-primary' : '']"
                            :fill="selectedLogLevelKeys.length > 0 ? 'currentColor' : 'none'" />
                        <span class="px-1">
                            {{ selectedLogLevelKeys.length > 0 ? `${selectedLogLevelKeys.length} selected` : 'Log levels' }}
                        </span>
                        <ChevronDown class="-mr-1 ml-2 h-5 w-5" />
                    </Button>
                </MultiSelect>
                <div class="flex gap-3 items-center">
                    <ConnectorFilter
                        v-model="selectedConnectors"
                        :connectors="connectors"
                        :loading="loading"
                        :show-tags="false"
                        placeholder="Filter by Connector"
                        :enable-filter-icon="true"
                        :include-all-option="false" />

                    <TimeRangeFilter
                        v-model="selectedTimeRange"
                        :loading="loading"
                        placeholder="Select time range"
                        :include-live-option="false"
                        :include-short-ranges="true"
                        :hide-options="[1209600, 2592000, 7776000, 31536000]"
                        @update:model-value="onTimeRangeChange" />
                </div>

                <Button
                    v-if="isPlayPauseButtonEnabled"
                    variant="default"
                    size="icon"
                    class="rounded-full my-1"
                    :class="{ 'pointer-events-none opacity-50': !isPlayPauseButtonEnabled }"
                    @click="onPlayPauseClick">
                    <Play v-if="playLiveFeed" class="h-5 w-5" />
                    <Pause v-else class="h-5 w-5" />
                </Button>
            </div>
        </section>

        <ScrollArea class="w-full border shadow-md rounded-md" :class="(logs.all?.length || 0) <= 10 ? 'h-auto' : 'h-70-dynamic'">
            <Table>
                <TableHeader class="sticky top-0 bg-background z-10">
                    <TableRow class="bg-muted/50">
                        <TableHead class="first:rounded-tl-md">Timestamp</TableHead>
                        <TableHead>Connector</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Level</TableHead>
                        <TableHead class="last:rounded-tr-md">Message</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    <!-- Skeleton rows when loading -->
                    <template v-if="loading">
                        <TableRow v-for="n in 10" :key="`skeleton-${n}`">
                            <TableCell><SkeletonLoader preset="table-metric" :loading="true" /></TableCell>
                            <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                            <TableCell><SkeletonLoader preset="table-type" :loading="true" /></TableCell>
                            <TableCell><SkeletonLoader preset="table-status" :loading="true" /></TableCell>
                            <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                        </TableRow>
                    </template>
                    <!-- Actual data rows -->
                    <template v-else>
                        <TableRow v-for="(log, i) in logs.all" :key="`${i}-${log.timestamp}`">
                            <TableCell class="font-mono text-xs">
                                {{ log.timestamp }}
                            </TableCell>
                            <TableCell class="font-mono text-xs">
                                <Button
                                    variant="link"
                                    class="h-auto p-0 max-w-full text-left justify-start font-mono text-xs"
                                    @click="router.push(selectedConnectorPath(log.connectorId, log.connectorContext))">
                                    <div class="flex gap-2 items-center">
                                        <Avatar class="w-5 h-5 bg-background dark:bg-slate-200 border border-border rounded-sm shrink-0">
                                            <AvatarImage
                                                :src="`/icons/${log.connectorType}.svg`"
                                                :title="log.connector"
                                                :alt="log.connectorContext"
                                                class="object-contain p-0.5" />
                                            <AvatarFallback
                                                class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-sm">
                                                {{ log.connectorType?.charAt(0).toUpperCase() }}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span class="hover:text-primary truncate" :title="log.connector">
                                            {{ log.connector }}
                                        </span>
                                    </div>
                                </Button>
                            </TableCell>
                            <TableCell class="font-mono text-xs">
                                <div class="flex items-center gap-2">
                                    <CloudUpload v-if="log.connectorContext === 'source'" class="h-4 w-4 text-muted-foreground" />
                                    <CloudDownload v-if="log.connectorContext === 'destination'" class="h-4 w-4 text-muted-foreground" />
                                    {{ log.connectorContext }}
                                </div>
                            </TableCell>
                            <TableCell class="font-mono text-xs" :class="getLevelColorClass(log.level)">
                                {{ log.level }}
                            </TableCell>
                            <TableCell class="font-mono text-xs cursor-pointer" @click="() => onViewMessageClick(log)">
                                <div class="flex items-center gap-3 text-muted-foreground hover:text-foreground">
                                    <ExternalLink class="shrink-0 h-4 w-4" />
                                    <img
                                        v-if="log.summary"
                                        src="/icons/streamkap_glitter.svg"
                                        class="h-5 w-5"
                                        alt="Summarized by Streamkap AI"
                                        title="Summarized by Streamkap AI" />
                                    <span class="truncate max-w-[200px] sm:max-w-[300px] md:max-w-[400px] lg:max-w-[500px]" :title="log.message">
                                        {{ log.message }}
                                    </span>
                                </div>
                            </TableCell>
                        </TableRow>

                        <TableEmpty v-if="!loading && !logs.all.length" :colspan="5">
                            <TableEmptyState />
                        </TableEmpty>
                    </template>
                </TableBody>
            </Table>
            <ScrollBar orientation="horizontal" />
            <ScrollBar orientation="vertical" />
        </ScrollArea>

        <TablePagination
            v-if="logs.all?.length || page > 0"
            :current-page="page"
            :page-size="pageSize"
            :total-items="999999"
            :current-page-items-count="logs.all?.length || 0"
            :filtered-count="searchQuery !== '' ? logs.all?.length : null"
            :has-next="logs.all?.length === pageSize"
            :show-total-items="false"
            item-type="logs"
            :loading="loading"
            @page-change="changePage"
            @page-size-change="onResultsPerPageChange" />
    </div>

    <!-- Log Message Dialog -->
    <LogMessageDialog
        :isOpen="logMessageDialogOpen"
        :message="selectedLog.message || ''"
        :isError="selectedLog.isError || false"
        :connectorName="selectedLog.connectorName"
        :namespaceName="selectedLog.namespaceName"
        :logLevel="selectedLog.logLevel"
        :timestamp="selectedLog.timestamp"
        :summary="selectedLog.summary || {}"
        :entityType="selectedLog.entityType"
        :logUuid="selectedLog.logUuid"
        :entityId="selectedLog.entityId"
        :showAiExplain="true"
        @update:isOpen="logMessageDialogOpen = $event" />
</template>

<script setup>
import { useLogsStore } from '../../stores/logs.store';
import LogMessageDialog from '../../components/LogMessageDialog.vue';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import ConnectorFilter from '@/components/filters/ConnectorFilter.vue';
import TimeRangeFilter from '@/components/filters/TimeRangeFilter.vue';
import { ref, computed, onMounted, watch } from 'vue';
import { Pause, Play, ExternalLink, CloudDownload, CloudUpload, Filter, ChevronDown } from 'lucide-vue-next';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableEmpty } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import TablePagination from '@/components/TablePagination.vue';
import SearchInput from '../../components/global/SearchInput.vue';
import moment from 'moment';
import { MultiSelect } from '@/components/ui/multi-select';
import _ from 'lodash';
import { TableEmptyState } from '@/components/ui/table-empty-state';
import { useRoute, useRouter } from 'vue-router';
import { useUrlFilters } from '@/composables/useUrlFilters';

const route = useRoute();
const router = useRouter();
const logs = useLogsStore();

const { registerFilter } = useUrlFilters({
    defaults: {
        // Pagination
        page: 0,
        pageSize: 50,

        // Search
        searchQuery: null,

        // Filters
        selectedConnectors: [],
        selectedTimeRange: 900, // 15 minutes
        selectedLogLevels: []
    },

    // Custom serializers for complex objects
    serializers: {
        searchQuery: (value) => value || null,
        selectedConnectors: (value) => JSON.stringify(value),
        selectedLogLevels: (value) => JSON.stringify(value)
    },

    // Custom deserializers
    deserializers: {
        searchQuery: (value) => value || null,
        selectedConnectors: (value) => {
            try {
                return value ? JSON.parse(value) : [];
            } catch {
                return [];
            }
        },
        selectedLogLevels: (value) => {
            try {
                return value ? JSON.parse(value) : [];
            } catch {
                return [];
            }
        }
    }
});

// Register all filters and get reactive references
const page = registerFilter('page', 0);
const pageSize = registerFilter('pageSize', 50);
const searchQuery = registerFilter('searchQuery', null);
const selectedConnectors = registerFilter('selectedConnectors', []);
const selectedTimeRange = registerFilter('selectedTimeRange', 900);
const selectedLogLevels = registerFilter('selectedLogLevels', []);

// Constants
const CONSTANTS = {
    all: { value: 'All', displayValue: 'All' },
    time: {
        live: 'Live',
        lastMin: 'Last min',
        last15Min: 'Last 15 min',
        last30Min: 'Last 30 min',
        lastHour: 'Last hour',
        lastDay: 'Last day',
        lastWeek: 'Last week'
    },
    levels: {
        debug: 'DEBUG',
        info: 'INFO',
        warn: 'WARN',
        error: 'ERROR'
    }
};

// Reactive state
const loading = ref(false);
const playLiveFeed = ref(false);
const isPlayPauseButtonEnabled = ref(false);
const listOfLogLevels = ref([
    { key: 'DEBUG', name: 'DEBUG' },
    { key: 'INFO', name: 'INFO' },
    { key: 'WARN', name: 'WARN' },
    { key: 'ERROR', name: 'ERROR' }
]);

// Log Message Dialog state
const logMessageDialogOpen = ref(false);
const selectedLog = ref({
    message: '',
    isError: false,
    connectorName: '',
    namespaceName: '',
    logLevel: '',
    timestamp: '',
    summary: {},
    entityType: '',
    entityId: ''
});

const selectedLogLevelKeys = computed({
    get() {
        return selectedLogLevels.value.map((level) => level.key);
    },
    set(keys) {
        selectedLogLevels.value = keys.map((key) => listOfLogLevels.value.find((level) => level.key === key)).filter(Boolean);
    }
});

const connectors = computed(() => {
    // For logs page, we want individual connector instances (not grouped by type)
    return (
        logs?.connectors?.map((conn) => ({
            id: conn.id,
            name: conn.name || conn.id,
            connectorDisplayName: conn.connectorDisplayName || conn.name || conn.id,
            connectorType: conn.connector,
            context: conn.context
        })) || []
    );
});

watch(loading, (newValue) => {
    if (newValue === true) {
        // Scroll to top of the main content area
        const mainElement = document.querySelector('main');
        if (mainElement) {
            mainElement.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }
});

watch(
    selectedLogLevels,
    _.debounce(async () => {
        page.value = 0;
        await fetchLogs();
    }, 1000),
    { deep: true }
);

watch(
    searchQuery,
    _.debounce(async () => {
        page.value = 0;
        await fetchLogs();
    }, 700)
);

watch(
    selectedConnectors,
    (newValue, oldValue) => {
        // Only fetch if the connectors actually changed (not just reactive updates)
        if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
            page.value = 0;
            fetchLogs();
        }
    },
    { deep: true }
);

onMounted(async () => {
    initializeFilters();
    await fetchLogs(true);
    initializeFromUrlParams();
});

// Functions
function selectedConnectorPath(id, type = 'source') {
    return type === 'source' ? { name: 'source-details', params: { id } } : { name: 'destination-details', params: { id } };
}

function initializeFilters() {
    // Set default values for new filter approach
    isPlayPauseButtonEnabled.value = selectedTimeRange.value === 0;
}

function initializeFromUrlParams() {
    // Handle URL parameters for initial connector selection
    const urlSourceId = route.query.source_id;
    const urlDestinationId = route.query.destination_id;

    if (urlSourceId || urlDestinationId) {
        const matchingConnectors = logs.connectors?.filter((connector) => connector.id === urlSourceId || connector.id === urlDestinationId);
        if (matchingConnectors && matchingConnectors.length > 0) {
            // For logs page, use individual connector IDs (not types)
            const connectorIds = matchingConnectors.map((conn) => conn.id);
            selectedConnectors.value = connectorIds;
        }
    }
}

function computeFetchParams(initialFetch = false) {
    const urlSourceId = route.query.source_id;
    const urlDestinationId = route.query.destination_id;
    const from = page.value * pageSize.value;
    const timestampFrom = moment().utc().subtract(selectedTimeRange.value, 'seconds').format('YYYY-MM-DDTHH:mm:ss.SSS');
    let sourceIds;
    let destinationIds;

    if (initialFetch) {
        sourceIds = urlSourceId ? [urlSourceId] : null;
        destinationIds = urlDestinationId ? [urlDestinationId] : null;
    } else if (selectedConnectors.value.length === 0) {
        // No connector filters selected - show all
        sourceIds = null;
        destinationIds = null;
    } else {
        // Filter by individual connector instances (IDs) - not connector types
        const selectedConnectorIds = selectedConnectors.value;
        const allConnectors = logs?.connectors || [];

        sourceIds = allConnectors.filter((conn) => conn.context === 'source' && selectedConnectorIds.includes(conn.id)).map((conn) => conn.id);

        destinationIds = allConnectors
            .filter((conn) => conn.context === 'destination' && selectedConnectorIds.includes(conn.id))
            .map((conn) => conn.id);
    }
    return {
        timestamp_from: timestampFrom,
        from,
        size: pageSize.value,
        source_ids: sourceIds?.length ? sourceIds : null,
        destination_ids: destinationIds?.length ? destinationIds : null,
        log_level: selectedLogLevels.value.map((level) => level.key).join(','),
        query_string: searchQuery.value || null
    };
}

async function fetchLogs(initialFetch = false) {
    // Prevent duplicate calls if already loading
    if (loading.value) {
        return;
    }

    loading.value = true;
    await logs.fetch(computeFetchParams(initialFetch));
    loading.value = false;
}

function changePage(newPage) {
    page.value = newPage;
    fetchLogs();
}

function onTimeRangeChange(timeRange) {
    selectedTimeRange.value = timeRange;
    isPlayPauseButtonEnabled.value = timeRange === 0; // Live mode
    page.value = 0;
    fetchLogs();
}

function onResultsPerPageChange(newSize) {
    pageSize.value = newSize;
    page.value = 0;
    fetchLogs();
}

function onPlayPauseClick() {
    playLiveFeed.value = !playLiveFeed.value;
}

function getLevelColorClass(level) {
    switch (level) {
        case CONSTANTS.levels.info:
            return 'text-green-600 dark:text-green-400';
        case CONSTANTS.levels.warn:
            return 'text-orange-600 dark:text-orange-400';
        case CONSTANTS.levels.error:
            return 'text-red-600 dark:text-red-400';
        default:
            return 'text-muted-foreground';
    }
}

async function onViewMessageClick(log) {
    // Set selected log data
    selectedLog.value = {
        message: log.message,
        isError: log.level === CONSTANTS.levels.error,
        connectorName: log.connectorChName,
        namespaceName: log.namespace,
        logLevel: log.level,
        timestamp: log.timestamp,
        summary: log.summary || {},
        entityType: log.connectorContext,
        entityId: log.connectorId,
        logUuid: log.logUuid
    };

    // Open the modal
    logMessageDialogOpen.value = true;
}
</script>
